import { Divider, LinearProgress } from '@mui/material'
import posthog from 'posthog-js'
import {
  EnrollementQuestionnaireSchema,
  FormFieldDto,
} from '../../interface/enrollmentApplication/EnrollementQuestionnaireSchema'
import {
  FormSubmissionData,
  submitFormProgrammatically,
} from '../../utils/FormSubmissionUtils'
import {
  getInitialFormValues,
  isFieldRequired,
} from '../../utils/EnrollmentApplicationFormUtils'
import {
  submitEnrollmentApplication,
  updateLatestMSEnrollmentApplicationStep,
} from '../../service/quotes'

import Button from '../common/Button/Button.component'
import CarrierLogo from '../common/CarrierLogo'
import DisclosureStepComponent from './DisclosureStepComponent'
import { FormFieldRenderer } from './FieldRenderer'
import { MsPlanDetails } from '../../interface/enrollmentApplication/MsPlanDetails'
import React from 'react'
import SectionRow from '../common/SectionRow'
import customerStore from '../../datastore/CustomerStore'
import styled from '@emotion/styled'
import theme from '../../global/theme'
import { useFormState } from './FormHook'

const StickyBar = styled.div`
  background-color: ${theme.colors.white};
  display: flex;
  justify-content: center;
  column-gap: 10px;
  position: sticky;
  bottom: 0;
  padding: 10px 0px;

  button {
    max-width: 150px;
  }

  @media screen and (max-width: 600px) {
    bottom: -20px;
    justify-content: space-between;
  }
`

interface EnrollmentQuestionnaireFormProps {
  questionnaireData: EnrollementQuestionnaireSchema
  planId: string
  forSpouse: boolean
  onSuccess: (enrollmentApplicationKey: string) => void
  onError: (errors: any) => void
  onStepChange?: (currentStep: number, totalSteps: number) => void
  loading: boolean
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  planDetails: MsPlanDetails
}

export const EnrollmentQuestionnaireForm: React.FC<
  EnrollmentQuestionnaireFormProps
> = ({
  questionnaireData,
  onSuccess,
  onError,
  onStepChange,
  planId,
  forSpouse,
  setLoading,
  planDetails,
}) => {
  const disclosureStepName = 'Final Step(Disclosure)'

  const [showDisclosureStep, setShowDisclosureStep] = React.useState(false)
  const [mothersMaidenName, setMothersMaidenName] = React.useState('')
  const [maidenNameError, setMaidenNameError] = React.useState('')
  const [calledSteps, setCalledSteps] = React.useState<Set<number>>(new Set())
  const [submittedSteps, setSubmittedSteps] = React.useState<Set<number>>(
    new Set()
  )
  const {
    formData,
    errors,
    currentStep,
    setCurrentStep,
    visibleSections,
    getVisibleFields,
    updateField,
    validateStep,
    getTransformedData,
  } = useFormState(
    questionnaireData,
    getInitialFormValues(customerStore, forSpouse, planDetails.planType)
  )

  // Call updateLatestMSEnrollmentApplicationStep for the initial step (step 0)
  React.useEffect(() => {
    if (
      visibleSections.length > 0 &&
      currentStep === 0 &&
      !calledSteps.has(0)
    ) {
      const updateInitialStep = async () => {
        try {
          const stepName = visibleSections[0]?.title || 'Step 1'
          await updateLatestMSEnrollmentApplicationStep(
            planId,
            forSpouse,
            stepName,
            1
          )
          setCalledSteps((prev) => new Set(prev).add(0))
          posthog.capture('eapp_step_entered', {
            step_name: stepName,
            step_number: 1,
            total_steps: visibleSections.length + 1,
            plan_id: planId,
            plan_name: planDetails.planName,
            plan_type: planDetails.planType,
            carrier: planDetails.carrier,
            for_spouse: forSpouse,
          })
        } catch (error) {
          console.error('Failed to update initial enrollment step:', error)
        }
      }
      updateInitialStep()
    }
  }, [visibleSections, planId, forSpouse, currentStep, calledSteps])

  React.useEffect(() => {
    if (
      visibleSections.length > 0 &&
      showDisclosureStep === true &&
      !calledSteps.has(visibleSections.length)
    ) {
      const updateDisclosureStep = async () => {
        try {
          await updateLatestMSEnrollmentApplicationStep(
            planId,
            forSpouse,
            disclosureStepName,
            visibleSections.length + 1
          )
          setCalledSteps((prev) => new Set(prev).add(visibleSections.length))
          posthog.capture('eapp_step_entered', {
            step_name: disclosureStepName,
            step_number: visibleSections.length + 1,
            total_steps: visibleSections.length + 1,
            plan_id: planId,
            plan_name: planDetails.planName,
            plan_type: planDetails.planType,
            carrier: planDetails.carrier,
            for_spouse: forSpouse,
          })
        } catch (error) {
          console.error('Failed to update disclosure enrollment step:', error)
        }
      }
      updateDisclosureStep()
    }
  }, [visibleSections, planId, forSpouse, calledSteps, showDisclosureStep])

  // Enhanced step change handler
  const handleStepChange = async (newStep: number) => {
    setCurrentStep(newStep)

    posthog.capture('eapp_step_entered', {
      step_name: visibleSections[newStep]?.title || `Step ${newStep + 1}`,
      step_number: newStep + 1,
      total_steps: visibleSections.length + 1,
      plan_id: planId,
      plan_name: planDetails.planName,
      plan_type: planDetails.planType,
      carrier: planDetails.carrier,
      for_spouse: forSpouse,
    })

    // Call updateLatestMSEnrollmentApplicationStep only if not already called for this step
    if (!calledSteps.has(newStep)) {
      try {
        const stepName =
          visibleSections[newStep]?.title || `Step ${newStep + 1}`
        await updateLatestMSEnrollmentApplicationStep(
          planId,
          forSpouse,
          stepName,
          newStep + 1
        )
        setCalledSteps((prev) => new Set(prev).add(newStep))
      } catch (error) {
        console.error('Failed to update enrollment step:', error)
      }
    }

    if (onStepChange) {
      onStepChange(newStep, visibleSections.length)
    }
  }

  const handleSubmit = async () => {
    try {
      setLoading(true)
      const requestBody = getTransformedData()
      requestBody['planId'] = planId
      requestBody['forSpouse'] = forSpouse
      requestBody['mothersMaidenName'] = mothersMaidenName
      const { data } = await submitEnrollmentApplication(requestBody)
      posthog.capture('eapp_step_submitted', {
        step_name: disclosureStepName,
        step_number: visibleSections.length + 1,
        total_steps: visibleSections.length + 1,
        plan_id: planId,
        plan_name: planDetails.planName,
        plan_type: planDetails.planType,
        carrier: planDetails.carrier,
        for_spouse: forSpouse,
      })
      onSuccess(data)
    } catch (error: any) {
      onError(error.message || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  const scrollToFirstError = (stepErrors?: Record<string, string>) => {
    setTimeout(() => {
      // Use passed stepErrors or current errors state
      const errorsToUse = stepErrors || errors
      const firstErrorField = visibleFields.find(
        (field) => errorsToUse[field.id]
      )
      if (firstErrorField) {
        const errorElement = document.querySelector(
          `[data-field-id="${firstErrorField.id}"]`
        )
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
      }
    }, 100)
  }

  const handleNext = async () => {
    if (showDisclosureStep) {
      // Validate mother's maiden name
      if (!mothersMaidenName.trim()) {
        setMaidenNameError("Mother's maiden name is required")
        return
      }
      setMaidenNameError('')
      // Submit form programmatically for the Final Step(Disclosure)
      const trackingData: FormSubmissionData = {
        stepName: disclosureStepName,
        stepNumber: visibleSections.length + 1,
        formData: formData,
        buttonId: `e-app-programatic-submit-step-${disclosureStepName.replace(
          /\s+/g,
          '-'
        )}`,
      }
      submitFormProgrammatically(trackingData, async () => {
        await handleSubmit()
      })
    } else {
      const validationResult = validateStep()
      if (validationResult.isValid) {
        // Only submit step programmatically if it hasn't been submitted before
        if (!submittedSteps.has(currentStep)) {
          const stepName = currentSection?.title || `Step ${currentStep + 1}`
          const trackingData: FormSubmissionData = {
            stepName,
            stepNumber: currentStep + 1,
            formData: formData,
            buttonId: `e-app-programatic-submit-step-${stepName.replace(
              /\s+/g,
              '-'
            )}`,
          }
          submitFormProgrammatically(trackingData, () => {
            // Mark this step as submitted
            setSubmittedSteps((prev) => new Set(prev).add(currentStep))

            posthog.capture('eapp_step_submitted', {
              step_name: stepName,
              step_number: currentStep + 1,
              total_steps: visibleSections.length + 1,
              plan_id: planId,
              plan_name: planDetails.planName,
              plan_type: planDetails.planType,
              carrier: planDetails.carrier,
              for_spouse: forSpouse,
            })

            if (currentStep < visibleSections.length - 1) {
              handleStepChange(currentStep + 1)
            } else {
              // Show disclosure step instead of submitting
              setShowDisclosureStep(true)

              if (onStepChange) {
                onStepChange(visibleSections.length, visibleSections.length + 1)
              }
            }
          })
        } else {
          // Step already submitted, just navigate
          if (currentStep < visibleSections.length - 1) {
            handleStepChange(currentStep + 1)
          } else {
            // Show disclosure step instead of submitting
            setShowDisclosureStep(true)
            if (onStepChange) {
              onStepChange(visibleSections.length, visibleSections.length + 1)
            }
          }
        }
      } else {
        // Validation failed, scroll to first error using the fresh errors
        scrollToFirstError(validationResult.stepErrors)
      }
    }
  }

  const handlePrevious = () => {
    if (showDisclosureStep) {
      setShowDisclosureStep(false)
      if (onStepChange) {
        onStepChange(currentStep, visibleSections.length)
      }
    } else if (currentStep > 0) {
      handleStepChange(currentStep - 1)
    }
  }

  const handleMothersMaidenNameChange = (value: string) => {
    // Remove whitespace from mother's maiden name
    const processedValue = value.replace(/\s/g, '')
    setMothersMaidenName(processedValue)
    if (maidenNameError) setMaidenNameError('')
  }

  const currentSection = visibleSections[currentStep]
  const visibleFields = currentSection
    ? getVisibleFields(currentSection, formData)
    : []

  if (showDisclosureStep) {
    return (
      <>
        <DisclosureStepComponent
          mothersMaidenName={mothersMaidenName}
          onMothersMaidenNameChange={handleMothersMaidenNameChange}
          error={maidenNameError}
        />

        {/*  Navigation */}
        <StickyBar>
          <Button onClick={handlePrevious}>Back</Button>
          <Button onClick={handleNext} variant='contained' color='secondary'>
            Submit
          </Button>
        </StickyBar>
      </>
    )
  }

  if (!currentSection) {
    return (
      <SectionRow style={{ textAlign: 'center' }}>
        <h3>Something went wrong</h3>
        <p>Please try again later!</p>
      </SectionRow>
    )
  }

  return (
    <>
      {/* Current Section */}
      <SectionRow>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            padding: '10px 0px',
          }}
        >
          <LinearProgress
            style={{ flex: '1' }}
            variant='determinate'
            value={((currentStep + 1) / visibleSections.length) * 100}
          />

          <span
            style={{
              color: theme.colors.textGrey,
              fontSize: '14px',
              minWidth: '80px',
            }}
          >
            Step {currentStep + 1} of {visibleSections.length}
          </span>
        </div>
        {currentStep === 0 && (
          <div
            style={{
              border: `1px solid ${theme.colors.borderColor}`,
              borderRadius: '8px',
              padding: '10px',
              background: theme.colors.backgroundColorContainer,
              margin: '20px 0px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <CarrierLogo carrier={planDetails.carrier} width='100' />{' '}
              <h3 style={{ color: theme.colors.primary, fontSize: '16px' }}>
                {planDetails.planName}
              </h3>
            </div>
            <div
              style={{
                marginTop: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
              }}
            >
              <p
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.colors.textGrey,
                }}
              >
                Monthly Premium :{' '}
                <span style={{ color: theme.colors.primary, fontSize: '16px' }}>
                  ${planDetails.monthlyPremium}
                </span>
              </p>
              <p
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.colors.textGrey,
                }}
              >
                Plan Type :{' '}
                <span style={{ color: theme.colors.primary, fontSize: '16px' }}>
                  {planDetails.planType}
                </span>
              </p>
            </div>
          </div>
        )}
        <h2 style={{ fontSize: '24px', color: theme.colors.primary }}>
          {currentSection.title}
        </h2>
        <p
          style={{
            color: theme.colors.textGrey,
            marginBottom: '20px',
          }}
        >
          {currentSection.heading}
          <Divider style={{ paddingTop: '10px' }} />
        </p>

        {visibleFields.map((field: FormFieldDto) => (
          <div key={field.id} data-field-id={field.id}>
            <FormFieldRenderer
              field={field}
              value={formData[field.id]}
              onChange={(value: any) => updateField(field.id, value)}
              error={errors[field.id]}
              formData={formData}
              updateField={updateField}
              isRequired={isFieldRequired(field, formData)}
            />
          </div>
        ))}
      </SectionRow>

      {/*  Navigation */}
      <StickyBar>
        <Button onClick={handlePrevious} disabled={currentStep === 0}>
          Back
        </Button>

        <Button onClick={handleNext} variant='contained' color='secondary'>
          Next
        </Button>
      </StickyBar>

      {/* Debug panel (development only) */}

      {/* <div
        style={{
          marginTop: '40px',
          padding: '20px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          fontSize: '12px',
        }}
      >
        <details>
          <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
            Debug Info (Development Only)
          </summary>
          <div style={{ marginTop: '10px' }}>
            <strong>Current Form Data:</strong>
            <pre>{JSON.stringify(formData, null, 2)}</pre>
            <strong>Current Errors:</strong>
            <pre>{JSON.stringify(errors, null, 2)}</pre>
            <strong>Visible Fields:</strong>
            <pre>
              {JSON.stringify(
                visibleFields.map((f: { id: any }) => f.id),
                null,
                2
              )}
            </pre>
          </div>
        </details>
      </div> */}
    </>
  )
}

export default EnrollmentQuestionnaireForm
